// Отображение состояния запроса к 1С: отказ по-человечески и свежесть данных.
//
// Зачем отдельно от QueryState. У 1С два состояния, которых нет у ядра:
//   • отказ с машинным кодом (timeout / busy / upstream / not_configured) — пустой экран
//     на его месте читался бы как «на складе ничего нет», а это разные вещи;
//   • «данные из кеша возрастом N» — у остатков это принципиально: они меняются в течение
//     смены, и решение по вчерашнему остатку — это несостоявшаяся отгрузка.
// Всё остальное (загрузка, 401, прочие ошибки) отдаём общему QueryState.
import type { ReactNode } from 'react'

import { OdataDown, fmtAge, odataMessage } from '../api/odata'
import type { OdataMeta } from '../api/odata'
import { QueryState } from './QueryState'

/** Пояснение к отказу: что человек может сделать сам. */
function hint(code: string): string {
  if (code === 'timeout' || code === 'busy') return 'Данные не потеряны — 1С просто занята. Повторите запрос.'
  if (code === 'not_configured' || code === 'core_unreachable') return 'Нужен администратор: со стороны интерфейса тут сделать нечего.'
  if (code === 'forbidden_entity') return 'Сущность добавляется в белый список ядра (ODATA_*_EXTRA) — скажите администратору, что именно нужно.'
  return 'Остатки в 1С сейчас недоступны. Пустой список ниже означал бы «товара нет» — поэтому мы его не показываем.'
}

export function OdataState({
  isLoading, error, children,
}: {
  isLoading: boolean
  error: unknown
  children: ReactNode
}) {
  if (error instanceof OdataDown) {
    return (
      <div className="card">
        <div className="error">{odataMessage(error)}</div>
        <div className="hint" style={{ marginTop: 8 }}>
          {hint(error.code)} <span className="mono">код: {error.code}</span>
        </div>
      </div>
    )
  }
  return <QueryState isLoading={isLoading} error={error}>{children}</QueryState>
}

/**
 * Свежесть данных 1С. Показываем всегда, а не только при проблемах: «остаток 12 шт.»
 * без возраста — это утверждение, которому нельзя проверить срок годности.
 */
export function OdataFreshness({
  meta, fetching, onRefresh,
}: {
  meta?: OdataMeta
  fetching?: boolean
  onRefresh?: () => void
}) {
  if (!meta) return null

  if (meta.stale) {
    return (
      <div className="notice">
        <b>1С не ответила</b> — показаны данные из кеша ядра возрастом {fmtAge(meta.ageSec)}.
        Остатки за это время могли измениться, отгрузку по ним подтверждать нельзя.
        {meta.error ? <div className="hint" style={{ marginTop: 6 }}>{meta.error}</div> : null}
        {onRefresh && (
          <div style={{ marginTop: 8 }}>
            <button onClick={onRefresh} disabled={fetching}>Попробовать ещё раз</button>
          </div>
        )}
      </div>
    )
  }

  const text = meta.cache === 'miss'
    ? 'данные только что из 1С'
    : `из кеша ядра, ${fmtAge(meta.ageSec)} назад${meta.ttlSec ? ` (обновляется раз в ${fmtAge(meta.ttlSec)})` : ''}`

  return (
    <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span>Остатки: {text}.</span>
      {onRefresh && (
        <button onClick={onRefresh} disabled={fetching} style={{ padding: '2px 8px', fontSize: 12 }}>
          {fetching ? 'обновляю…' : 'обновить'}
        </button>
      )}
    </div>
  )
}
